import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FormGroup, Label, Input, Alert, Card, CardHeader, CardBody, Row, Col, Button, Form } from 'reactstrap';
import { inputId, inputName, formatToCurrency, drawTooltipHelp } from '../../helper/form';
import FieldError from '../../helper/FieldError';

import Select from 'react-select';

import ComplianceActivityResourceModuleSelect from './resource_module_select';
import ComplianceActivityRecurrencePreviewModal from './recurrence_preview_modal';
import ComplianceActivityGroupList from './group/list';
import ComplianceActivityEvidenceList from './evidence/list';
import SelectEmailNotifications from '../../abstract/bulk/request/document_config/select_email_notifications';

import { create as createActivityService, update as updateActivityService } from './axios';
import axios from 'axios';

const getFormData = (props) => {
  const {
    activity,
    formName,
    customFields,
  } = props;

  let formData = new FormData();

  formData.append(`${ formName }[id]`, activity?.id || '')
  formData.append(`${ formName }[name]`, activity?.name || '')
  formData.append(`${ formName }[description]`, activity?.description || '')
  formData.append(`${ formName }[compliance_risk_level_id]`, activity?.compliance_risk_level_id || '')
  formData.append(`${ formName }[start_date]`, activity?.start_date || moment().format('YYYY-MM-DD'))
  formData.append(`${ formName }[deadline]`, activity?.deadline || moment().add(1, 'month').format('YYYY-MM-DD'))
  formData.append(`${ formName }[recurrence]`, activity?.recurrence || '')
  formData.append(`${ formName }[resource_module_name]`, activity?.resource_module_name || '')
  formData.append(`${ formName }[resource_class]`, props?.selectedResourceClass || '')
  formData.append(`${ formName }[abstract_entity_type_id]`, activity?.abstract_entity_type_id || '')
  formData.append(`${ formName }[assign_uploaders_by_email_notification]`, activity?.assign_uploaders_by_email_notification || false)
  formData.append(`${ formName }[email_notification_custom_field_id]`, activity?.email_notification_custom_field_id || '')

  _.each(customFields, function(customField){
    let value = activity?.custom_fields[customField.code] || '';
    formData.append(`${ formName }[custom_fields][${ customField.code }]`, value);
  })

  _.each(activity?.active_filters || {}, (value, key) => {
    formData.append(`${ formName }[active_filters][${ key }]`, value)
  })

  _.each(activity?.reviewer_groups, (reviewerGroup, index) => {
    formData.append(`${ formName }[reviewer_groups_attributes][${ index }][id]`, reviewerGroup?.id || '')
    formData.append(`${ formName }[reviewer_groups_attributes][${ index }][compliance_group_id]`, reviewerGroup?.compliance_group_id || '')
    formData.append(`${ formName }[reviewer_groups_attributes][${ index }][mandatory]`, reviewerGroup?.mandatory || false)
    formData.append(`${ formName }[reviewer_groups_attributes][${ index }][_destroy]`, reviewerGroup?._destroy || '')
  })

  _.each(activity?.uploader_groups, (uploaderGroup, index) => {
    formData.append(`${ formName }[uploader_groups_attributes][${ index }][id]`, uploaderGroup?.id || '')
    formData.append(`${ formName }[uploader_groups_attributes][${ index }][compliance_group_id]`, uploaderGroup?.compliance_group_id || '')
    formData.append(`${ formName }[uploader_groups_attributes][${ index }][_destroy]`, uploaderGroup?._destroy || '')
  })

  _.each(activity?.consultant_groups, (consultantGroup, index) => {
    formData.append(`${ formName }[consultant_groups_attributes][${ index }][id]`, consultantGroup?.id || '')
    formData.append(`${ formName }[consultant_groups_attributes][${ index }][compliance_group_id]`, consultantGroup?.compliance_group_id || '')
    formData.append(`${ formName }[consultant_groups_attributes][${ index }][_destroy]`, consultantGroup?._destroy || '')
  })

  _.each(activity?.informer_groups, (informerGroup, index) => {
    formData.append(`${ formName }[informer_groups_attributes][${ index }][id]`, informerGroup?.id || '')
    formData.append(`${ formName }[informer_groups_attributes][${ index }][compliance_group_id]`, informerGroup?.compliance_group_id || '')
    formData.append(`${ formName }[informer_groups_attributes][${ index }][_destroy]`, informerGroup?._destroy || '')
  })

  _.each(activity?.evidences, (evidence, index) => {
    formData.append(`${ formName }[evidences_attributes][${ index }][id]`, evidence?.id || '')
    formData.append(`${ formName }[evidences_attributes][${ index }][name]`, evidence?.name || '')
    formData.append(`${ formName }[evidences_attributes][${ index }][is_recurrent]`, evidence?.is_recurrent || false)
    formData.append(`${ formName }[evidences_attributes][${ index }][_destroy]`, evidence?._destroy || '')

    if(evidence?.template && evidence.template instanceof File){
      formData.append(`${ formName }[evidences_attributes][${ index }][template]`, evidence.template)
    }

    _.each(evidence?.children || [], (child, childIndex) => {
      formData.append(`${ formName }[evidences_attributes][${ index }][children_attributes][${ childIndex }][id]`, child?.id || '')
      formData.append(`${ formName }[evidences_attributes][${ index }][children_attributes][${ childIndex }][name]`, child?.name || '')
      formData.append(`${ formName }[evidences_attributes][${ index }][children_attributes][${ childIndex }][is_recurrent]`, child?.is_recurrent || false)
      formData.append(`${ formName }[evidences_attributes][${ index }][children_attributes][${ childIndex }][_destroy]`, child?._destroy || '')

      if(child?.template && child.template instanceof File){
        formData.append(`${ formName }[evidences_attributes][${ index }][children_attributes][${ childIndex }][template]`, child.template)
      }
    })
  })

  return formData;
}

const ComplianceActivityForm = (props) => {
  const {
    complianceContext,
    data: {
      recurrences,
      riskLevels,
      customFields,
      groups,
      moduleGroupedOptions
    },
    configuration: {
      formName,
      defaultRequestParams
    },
    callbacks
  } = props;

  const {
    onChangeActivities: callbackOnChangeActivities
  } = callbacks || {};

  const [activity, setActivity] = useState(props?.activity || {});
  const [abstractEntityType, setAbstractEntityType] = useState({});

  const recurrenceSelectOptions = useMemo(() => {
    return _.map(recurrences, recurrence => {
      return {
        value: recurrence,
        label: I18n.t(`activerecord.attributes.compliance/activity.recurrence.${recurrence}`)
      };
    });
  }, []);

  const recurrenceSelectedOption = useMemo(() => {
    let selected = _.find(recurrenceSelectOptions, { value: activity?.recurrence })

    if (selected) {
      return { value: selected.value, label: selected.label }
    } else {
      return null
    }
  }, [activity?.recurrence])

  const riskLevelSelectOptions = useMemo(() => {
    return _.map(riskLevels, riskLevel => {
      return {
        value: riskLevel.id,
        label: riskLevel.name
      };
    });
  }, []);

  const riskLevelSelectedOption = useMemo(() => {
    let selected = _.find(riskLevelSelectOptions, { value: activity?.compliance_risk_level_id })

    if (selected) {
      return { value: selected.value, label: selected.label }
    } else {
      return null
    }
  }, [activity?.compliance_risk_level_id])

  const selectedResourceLabel = useMemo(() => {
    return _.chain(moduleGroupedOptions)
      .flatMap('options')
      .find({ value: activity?.resource_module_name })
      .get('label', '')
      .value();
  }, [activity?.resource_module_name]);

  const selectedResourceClass = useMemo(() => {
    return _.chain(moduleGroupedOptions)
      .flatMap('options')
      .find({ value: activity?.resource_module_name })
      .get('resource_class', '')
      .value();
  }, [activity?.resource_module_name]);

  // ---------- START ONCHANGE ACTIVITY ----------
  const onChangeActivity = (event, key) => {
    let value = event.target.value;

    if(event.target.type == 'checkbox'){
      value = event.target.checked;
    }

    if(key === 'abstract_entity_type_id' && value === ''){
      setActivity(prevState => {
        return { ... prevState, assign_uploaders_by_email_notification: false }
      })
    }

    setActivity(prevState => {
      return { ... prevState, [key]: value }
    })
  }

  // ---------- END ONCHANGE ACTIVITY ----------

  const getAbstractEntityTypeService = async () => {
    const response = await axios({
      method: 'get',
      url: `/api/v1/abstract/entity_types/${activity?.abstract_entity_type_id}.json`,
      headers: {
        "X-CSRF-TOKEN": window.compliance.token,
        'Entity-Type-Id': activity?.abstract_entity_type_id,
        'Customer-Id': activity?.customer_id,
      },
    });

    return response;
  }

  const fetchAbstractEntityType = useCallback(() => {
    if(activity?.abstract_entity_type_id){
      getAbstractEntityTypeService().then(response => {
        if(response.status === 200) {
          setAbstractEntityType(response.data);
        }
      }).catch(error => {
        console.error('Error fetching abstract entity type:', error);
      });
    }
  }, [activity?.abstract_entity_type_id]);

  useEffect(() => {
    fetchAbstractEntityType();
  }, [activity?.abstract_entity_type_id]);

  const customFieldEmailNotifications = useMemo(() => {
    if(!abstractEntityType) return {};

    const entityTypes = [
      ...(abstractEntityType?.related_entity_types || []),
      abstractEntityType
    ]

    let emailCustomFields = {}

    _.each(entityTypes, entityType => {
      const customFieldEmailNotifications = _.filter(
        entityType?.custom_fields, { is_notification_email: true }
      )

      if (customFieldEmailNotifications.length > 0) {
        emailCustomFields[entityType.plural_name] = customFieldEmailNotifications
      }
    })

    return emailCustomFields;
  }, [abstractEntityType])

  // ---------- START ONCHANGE CUSTOM FIELDS ----------
  const onChangeCustomFields = (e, customField) => {
    let value = e?.target?.value;
    let key = customField.code;

    if(customField.data_type == 'currency'){
      value = formatToCurrency(value)
    }

    setActivity(prevState => {
      return { ...prevState, custom_fields: { ...prevState.custom_fields, [key]: value }}
    })
  }

  // ---------- END ONCHANGE CUSTOM FIELDS ----------

  // ---------- START ONCONFIRM SUBMIT ----------
  const onConfirmSubmit = (event) => {
    event.preventDefault();

    // Valida que si hay un módulo seleccionado, debe tener al menos un registro
    if(activity?.resource_module_name && activity?.total_entity_records === 0){
      swalWithBootstrap.fire({
        title: I18n.t('compliance.activities.messages.resource_module.without_records.confirm.title'),
        html: I18n.t('compliance.activities.messages.resource_module.without_records.confirm.message'),
        icon: 'warning',
        confirmButtonText: 'Entendido'
      });
      return;
    }

    swalWithBootstrap.fire({
      title: I18n.t('compliance.activities.form.confirm.title'),
      html: I18n.t('compliance.activities.form.confirm.message'),
    }).then( result => {
      if(result.isConfirmed){
        onSubmit()
      }
    })
  }

  const onSubmit = () => {
    const formData = getFormData({formName, activity, customFields, selectedResourceClass});
    const requestParams = { ... defaultRequestParams, id: activity.id }

    const saveService = activity.id ? updateActivityService : createActivityService;

    saveService(requestParams, formData, response => {
      if(response.status == 200 || response.status == 201){
        setActivity(response.data);

        swalWithBootstrap.fire({
          title: I18n.t(`compliance.activities.${ activity.id ? 'update' : 'create'}.successfully`),
          icon: 'success',
          showConfirmButton: false,
          timer: 1500
        })

        if(_.isFunction(callbackOnChangeActivities)){
          callbackOnChangeActivities(response.data);
        } else {
          redirectToActivity(response.data);
        }
      }else{
        setActivity(response.data);

        _.each(response.data.errors, error => {
          swalToastBootstrap.fire({
            icon: 'error',
            title: error
          });
        })
      }
    })
  }

  const redirectToActivity = (activity) => {
    setTimeout(() => {
      if(activity.is_template){
        window.location = Routes.compliance_activities_path({...defaultRequestParams});
      } else {
        window.location = Routes.compliance_activity_path(activity.hashid, {...defaultRequestParams});
      }
    }, 1500);
  }

  // ---------- START RISK LEVEL ALERT ----------
  const riskLevelAlert = () => {
    if(riskLevels?.length == 0){
      return (
        <Alert color="warning">
          { I18n.t('compliance.activities.form.alert_risk_level') }
          <a href={ Routes.new_compliance_risk_level_path(defaultRequestParams) } className="text-blue">¡Crear uno aquí!</a>
        </Alert>
      )
    }
  }

  // ---------- START NAME ----------
  const nameInput = () => {
    return (
      <FormGroup>
        <FieldError errors={ activity?.errors?.name || [] }>
          <Label
            for={ inputId(formName, 'name') }
            className='required'
          >
            { I18n.t("activerecord.attributes.compliance/activity.name") }
          </Label>
          <Input
            type='text'
            id={ inputId(formName, 'name') }
            name={ inputName(formName, 'name') }
            invalid={ _.has(activity?.errors, 'name') }
            value={ activity?.name || '' }
            onChange={ e => onChangeActivity(e, 'name') }
          />
          <small className="text-muted">{ I18n.t('activerecord.help.compliance/activity.name') }</small>
        </FieldError>
      </FormGroup>
    )
  }

  // ---------- START DESCRIPTION ----------
  const descriptionInput = () => {
    return (
      <FormGroup>
        <FieldError errors={ activity?.errors?.description || [] }>
          <Label for={ inputId(formName, 'description') } className='required'>
            { I18n.t("activerecord.attributes.compliance/activity.description") }
          </Label>
          <Input
            type='textarea'
            id={ inputId(formName, 'description') }
            name={ inputName(formName, 'description') }
            invalid={ _.has(activity?.errors, 'description') }
            value={ activity?.description || '' }
            onChange={ e => onChangeActivity(e, 'description') }
          />
          <small className="text-muted">{ I18n.t('activerecord.help.compliance/activity.description') }</small>
        </FieldError>
      </FormGroup>
    )
  }

  // ---------- START RISKLEVEL ----------
  const riskLevelSelectInput = () => {
    return (
      <FormGroup>
        <FieldError errors={ activity?.errors?.risk_level || [] }>
          <Label for={ inputId(formName, 'compliance_risk_level_id') } className='required'>
            { I18n.t("activerecord.attributes.compliance/activity.compliance_risk_level_id") }
          </Label>
          <Select
            id={inputId(formName, 'compliance_risk_level_id')}
            name={inputName(formName, 'compliance_risk_level_id')}
            value={riskLevelSelectedOption}
            onChange={e => onChangeActivity({ target: { value: e?.value } }, 'compliance_risk_level_id')}
            options={riskLevelSelectOptions}
            invalid={_.has(activity?.errors, 'risk_level')}
            placeholder={`-- Selecciona un ${I18n.t('activerecord.attributes.compliance/activity.compliance_risk_level_id')} --`}
          />
          <small className="text-muted">{ I18n.t('activerecord.help.compliance/activity.compliance_risk_level_id') }</small>
        </FieldError>
      </FormGroup>
    )
  }

  // ---------- START RESOURCE MODULE ----------
  const resourceModuleSelectInput = () => {
    // Solo se debe asignar un módulo si es una nueva actividad
    if(activity?.id) return null;

    return (
      <Card>
        <CardHeader>
          <h5 className='font-weight-bold mb-1 d-inline'>
            { I18n.t('activerecord.attributes.compliance/activity.resource_module_name') }
          </h5>
          { drawTooltipHelp(I18n.t('compliance.activities.tooltip_resource_module_name')) }
          <small className="text-muted d-block">
            { I18n.t('compliance.activities.tooltip_resource_module_name') }
          </small>
        </CardHeader>
        <CardBody>
          <ComplianceActivityResourceModuleSelect
            activity={activity}
            formName={formName}
            data={{
              resourceLabel: selectedResourceLabel,
              moduleGroupedOptions: moduleGroupedOptions
            }}
            callbacks={{
              onChangeActivity: onChangeActivity
            }}
          />
        </CardBody>
      </Card>
    )
  }

  // ---------- START START DATE ----------
  const startDateDatepickerInput = () => {
    return (
      <FormGroup>
        <FieldError errors={ activity?.errors?.start_date || [] }>
          <Label for={ inputId(formName, 'start_date') } className='required'>
            { I18n.t("activerecord.attributes.compliance/activity.start_date") }
          </Label>
          <Input
            type='date'
            id={ inputId(formName, 'start_date') }
            name={ inputName(formName, 'start_date') }
            invalid={ _.has(activity?.errors, 'start_date') }
            value={ activity?.start_date ? moment(activity?.start_date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD') }
            onChange={ e => onChangeActivity(e, 'start_date') }
          />
          <small className="text-muted">{ I18n.t('activerecord.help.compliance/activity.start_date') }</small>
        </FieldError>
      </FormGroup>
    )
  }

  // ---------- START DEADLINE ----------
  const deadlineDatepickerInput = () => {
    return (
      <FormGroup>
        <FieldError errors={ activity?.errors?.deadline || [] }>
          <Label for={ inputId(formName, 'deadline') } className='required'>
            { I18n.t("activerecord.attributes.compliance/activity.deadline") }
          </Label>
          <Input
            type='date'
            id={ inputId(formName, 'deadline') }
            name={ inputName(formName, 'deadline') }
            invalid={ _.has(activity?.errors, 'deadline') }
            value={ activity?.deadline ? moment(activity?.deadline).format('YYYY-MM-DD') : moment().add(1, 'month').format('YYYY-MM-DD') }
            onChange={ e => onChangeActivity(e, 'deadline') }
          />
          <small className="text-muted">{ I18n.t('activerecord.help.compliance/activity.deadline') }</small>
        </FieldError>
      </FormGroup>
    )
  }

  // ---------- START RECURRENCE ----------
  const recurrenceSelectInput = () => {
    return (
      <FormGroup>
        <FieldError errors={ activity?.errors?.recurrence || [] }>
          <Label for={ inputId(formName, 'recurrence') } className='required'>
            { I18n.t("activerecord.attributes.compliance/activity.recurrence.one") }
          </Label>
          <Select
            id={inputId(formName, 'recurrence')}
            name={inputName(formName, 'recurrence')}
            value={recurrenceSelectedOption}
            onChange={e => onChangeActivity({ target: { value: e?.value } }, 'recurrence')}
            options={recurrenceSelectOptions}
            invalid={_.has(activity?.errors, 'recurrence')}
            placeholder={`-- Selecciona un ${I18n.t('activerecord.attributes.compliance/activity.recurrence.one')} --`}
          />
          <small className="text-muted">{ I18n.t('activerecord.help.compliance/activity.recurrence') }</small>
        </FieldError>
      </FormGroup>
    )
  }

  // ---------- START GANTT RECURRENCE MODAL ----------
  const recurrencePreviewModal = () => {
    return (
      <ComplianceActivityRecurrencePreviewModal
        activity={activity}
      />
    )
  }

  // ---------- START CUSTOMFIELDS ----------
  const renderCustomFields = () => {
    if(customFields?.length > 0){
      return (
        <Card>
          <CardHeader>
            <h5 className='font-weight-bold mb-1 d-inline'>
              { I18n.t('compliance.activities.custom_fields') }
            </h5>
          </CardHeader>
          <CardBody>
            {
              _.map(customFields, function(customField){
                const value = customField.data_type == 'date' ? moment(activity.custom_fields[customField.code], 'YYYY-MM-DD').format('YYYY-MM-DD') : activity.custom_fields[customField.code]
                return customFieldSelectInput(customField, value)
              })
            }
          </CardBody>
        </Card>
      )
    }
  }

  const customFieldSelectInput = (customField, value) => {
    let inputClass = customField.data_type === 'currency' ? 'currency-field' : '';

    if(customField.data_type == 'list'){
      return(
        <FormGroup className="col-12" key={ customField.code }>
          <Label for={ inputId(formName, `custom_fields_${ customField.code }`) }>
            { _.startCase(customField.label) }
          </Label>
          <Select
            id={ inputId(formName, `custom_fields_${ customField.code }`) }
            name={ inputName(`${ formName }[custom_fields]`, `${ customField.code }`) }
            invalid={ _.has(activity?.errors, `custom_fields_${ customField.code }`) }
            onChange={ e => onChangeCustomFields({ target: { value: e?.value } }, customField ) }
            value={ { label: value, value: value } || '' }
            options={ _.map(customField.list_options, option => ({ label: option, value: option })) }
            type={ customField.data_type }
            placeholder={ `-- Selecciona una opción --` }
          />
        </FormGroup>
      )
    }else{
      return(
        <FormGroup className="col-6" key={ customField.code }>
          <Label for={ inputId(formName, `custom_fields_${ customField.code }`) }>
            { _.startCase(customField.label) }
          </Label>
          <Input
            id={ inputId(formName, `custom_fields_${ customField.code }`) }
            name={ inputName(`${ formName }[custom_fields]`, `${ customField.code }`) }
            invalid={ _.has(activity?.errors, `custom_fields_${ customField.code }`) }
            onChange={ (e) => onChangeCustomFields(e, customField ) }
            className={ inputClass }
            value={ value }
            type={ customField.data_type }
          />
        </FormGroup>
      )
    }
  }

  // ---------- START ACTIVITY GROUPS ----------
  const renderUploaderAndReviewerGroups = () => {
    let activityGroupsNames = ['uploader_groups', 'reviewer_groups'];

    return _.map(activityGroupsNames, (activityGroupName) => {
      return (
        <Card key={`activityGroupCard-${activityGroupName}`}>
          <CardHeader>
            <Label>
              { I18n.t(`activerecord.attributes.compliance/activity.${activityGroupName}`) }
            </Label>
            <button type="button" className="btn btn-link p-0 ml-2" data-bs-placement="right" data-bs-html="true" data-bs-toggle="tooltip" title={I18n.t(`compliance.activities.tooltip_groups.${activityGroupName}`)}>
              <i className="fas fa-info-circle text-info fa-lg"></i>
            </button>
          </CardHeader>
          <CardBody>
            { activityGroupName === 'uploader_groups' && assignUploadersByEmailNotificationSwitchInput() }
            { ActivityGroupsSelectInput(activityGroupName) }
          </CardBody>
        </Card>
      )
    })
  }

  const renderConsultantAndInformerGroups = () => {
    let activityGroupsNames = ['consultant_groups', 'informer_groups'];

    return _.map(activityGroupsNames, (activityGroupName) => {
      return (
        <Card key={`activityGroupCard-${activityGroupName}`}>
          <CardHeader>
            <Label>
              { I18n.t(`activerecord.attributes.compliance/activity.${activityGroupName}`) }
            </Label>
            <button type="button" className="btn btn-link p-0 ml-2" data-bs-placement="right" data-bs-html="true" data-bs-toggle="tooltip" title={I18n.t(`compliance.activities.tooltip_groups.${activityGroupName}`)}>
              <i className="fas fa-info-circle text-info fa-lg"></i>
            </button>
          </CardHeader>
          <CardBody>
            { ActivityGroupsSelectInput(activityGroupName) }
          </CardBody>
        </Card>
      )
    })
  }

  const ActivityGroupsSelectInput = (groupName) => {
    let activityGroups = activity[`${ groupName }`] || [];

    if(groupName === 'uploader_groups' && activity?.assign_uploaders_by_email_notification) return null;

    return (
      <ComplianceActivityGroupList
        activityGroups={activityGroups}
        groups={groups}
        configuration={{
          formName: formName[`${ groupName }_attributes`],
          groupName: groupName
        }}
        callbacks={{
          onChangeActivity: onChangeActivity
        }}
      />
    )
  }

  const assignUploadersByEmailNotificationSwitchInput = () => {
    if(activity?.abstract_entity_type_id){
      return (
        <Row>
          <FormGroup>
            <div className='custom-switch'>
              <FieldError errors={ activity?.errors?.assign_uploaders_by_email_notification || [] }>
                <Input
                  className='custom-control-input'
                  type='checkbox'
                  name={ inputName(formName, 'assign_uploaders_by_email_notification') }
                  id={ inputId(formName, 'assign_uploaders_by_email_notification') }
                  invalid={ _.has(activity?.errors, 'assign_uploaders_by_email_notification') }
                  onChange={ e => onChangeActivity(e, 'assign_uploaders_by_email_notification') }
                  checked={ activity?.assign_uploaders_by_email_notification || false }
                  value={ '1' }
                  style={{ display: 'contents' }}
                />
                <Label
                  className='custom-control-label'
                  for={ inputId(formName, 'assign_uploaders_by_email_notification') }
                >
                  { I18n.t(`activerecord.attributes.compliance/activity.assign_uploaders_by_email_notification`) }
                  { drawTooltipHelp(I18n.t('compliance.activities.tooltip_assign_uploaders_by_email_notification')) }
                </Label>
              </FieldError>
            </div>
          </FormGroup>

          {
            activity?.assign_uploaders_by_email_notification && (
              <div className="mt-3">
                <SelectEmailNotifications
                  value={activity?.email_notification_custom_field_id || ""}
                  handleConfigData={e => onChangeActivity(e, 'email_notification_custom_field_id')}
                  customFieldEmailNotifications={customFieldEmailNotifications}
                  errors={activity?.errors || {}}
                />
              </div>
            )
          }
        </Row>
      )
    }
  }

  // ---------- START EVIDENCES ----------
  const renderEvidences = () => {
    let mainEvidences = _.filter(activity?.evidences, (evidence) => {
      return !evidence?.parent_id && !evidence?._destroy
    });

    return (
      <Card>
        <CardHeader>
          <h5 className='font-weight-bold mb-1 d-inline'>
            { I18n.t('activerecord.models.compliance/evidence.other') }
          </h5>
          <button type="button" className="btn btn-link p-0 ml-2" data-bs-placement="right" data-bs-html="true" data-bs-toggle="tooltip" title={I18n.t("compliance.activities.tooltip_evidences.info")}>
            <i className="fas fa-info-circle text-info fa-lg"></i>
          </button>
          <small className="text-muted d-block">
            { I18n.t("compliance.activities.tooltip_evidences.info") }
          </small>
        </CardHeader>
        <CardBody>
          <ComplianceActivityEvidenceList
            evidences={mainEvidences}
            configuration={{
              formName: `${ formName }[evidences_attributes]`,
            }}
            callbacks={{
              onChangeActivity: onChangeActivity
            }}
          />
        </CardBody>
      </Card>
    )
  }
  // ---------- END EVIDENCES ----------

  return (
    <Form onSubmit={onConfirmSubmit}>
      <Row>
        <Col md={12}>
          { riskLevelAlert() }

          <Card>
            <CardHeader>
              <h5 className='font-weight-bold mb-1 d-inline'>
                { I18n.t('compliance.activities.form.title') }
              </h5>
            </CardHeader>
            <CardBody>
              { nameInput() }
              { descriptionInput() }
              { riskLevelSelectInput() }
            </CardBody>
          </Card>

          { renderCustomFields() }

          { complianceContext?.enabled_resource_config && resourceModuleSelectInput() }

          <Card>
            <CardHeader>
              <h5 className='font-weight-bold mb-1 d-inline'>
                Configuración
              </h5>
            </CardHeader>
            <CardBody>
              { startDateDatepickerInput() }
              { deadlineDatepickerInput() }
              { recurrenceSelectInput() }
              { recurrencePreviewModal() }
            </CardBody>
          </Card>

          { renderUploaderAndReviewerGroups() }

          <Card>
            <CardHeader className='__with_collapsible-icons'>
              <a className='font-weight-bold text-dark text-decoration-none d-block' data-bs-toggle='collapse' href='#collapseAdvancedConfiguration' role='button'>
                <i className='fas fa-cog mr-2'></i>
                Configuración de avanzada
              </a>
            </CardHeader>
            <div className='collapse border-top mx-2 mt-2' id='collapseAdvancedConfiguration'>
              { renderConsultantAndInformerGroups() }
            </div>
          </Card>

          { renderEvidences() }
        </Col>

        <div className="text-end">
          <Button type="submit" className="btn btn-success">
            {I18n.t('actions.save')}
          </Button>
        </div>
      </Row>
    </Form>
  );
};

export default ComplianceActivityForm;